// AudioManager.ts — Singleton Audio Controller for Phaser 3
// Uses the game-level sound manager so audio works across scene transitions.
// Supports: audio sprites (sound effects) + background music (separate instance)

import { Assets } from "../Assets"; // adjust path if needed

type InitConfig = {
  scene: Phaser.Scene;
  isMuted?: boolean;
  onMuteStatusChange?: (isMuted: boolean) => void;
};

interface PlayingSoundEntry {
  id: string;
  marker: string;
  blockTime: number;
  lastPlayedTime: number;
  pitch: number;
  pitchChangeDelay: number;
  sound: Phaser.Sound.BaseSound | null;
}

export default class AudioManager {
  // --- Singleton setup ---
  private static _instance: AudioManager | null = null;

  private _scene: Phaser.Scene;
  private _sound: Phaser.Sound.BaseSoundManager; // game-level sound manager
  private _isMuted = false;

  // arrays & references
  private _playingSounds: PlayingSoundEntry[] = [];

  // dedicated references for separate audio behaviour
  private _sfxSprite: Phaser.Sound.BaseSound | null = null; // used for short sound effects (markers)
  private _musicSprite: Phaser.Sound.BaseSound | null = null; // used for music playback (marker or separate key)
  private _music: Phaser.Sound.BaseSound | null = null; // currently-playing music reference

  private _onMuteStatusChange?: (isMuted: boolean) => void;

  private constructor(scene: Phaser.Scene) {
    this._scene = scene;
    // Use the game-level sound manager so audio persists across scenes.
    // scene.sys.game.sound is safe after scenes change.
    // Type uses BaseSoundManager which covers the sound plugin.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._sound = scene.sys?.game?.sound ?? scene.sound;
  }

  /**
   * Initialize AudioManager (call once in your first scene)
   * Accepts either a Phaser.Scene or an InitConfig object.
   */
  public static init(sceneOrConfig: Phaser.Scene | InitConfig): AudioManager {
    let scene: Phaser.Scene;
    let initialMuted = false;
    let onMuteCallback: ((isMuted: boolean) => void) | undefined;

    if ((sceneOrConfig as InitConfig).scene) {
      const cfg = sceneOrConfig as InitConfig;
      scene = cfg.scene;
      initialMuted = !!cfg.isMuted;
      onMuteCallback = cfg.onMuteStatusChange;
    } else {
      scene = sceneOrConfig as Phaser.Scene;
    }

    if (!this._instance) {
      this._instance = new AudioManager(scene);
      this._instance._isMuted = initialMuted;
      if (initialMuted) this._instance._sound.mute = true;
      if (onMuteCallback) this._instance._onMuteStatusChange = onMuteCallback;
    } else {
      // update existing instance with any provided config
      if ((sceneOrConfig as InitConfig).scene) {
        const cfg = sceneOrConfig as InitConfig;
        if (typeof cfg.isMuted !== "undefined") {
          this.instance._isMuted = !!cfg.isMuted;
          this.instance._sound.mute = !!cfg.isMuted;
        }
        if (cfg.onMuteStatusChange) {
          this.instance._onMuteStatusChange = cfg.onMuteStatusChange;
        }
      }
    }

    return this._instance;
  }

  /** Access the singleton instance (after init) */
  public static get instance(): AudioManager {
    if (!this._instance) {
      throw new Error("AudioManager not initialized. Call AudioManager.init(scene) first.");
    }
    return this._instance;
  }

  // ===========================================================
  // 🔊 SOUND EFFECTS (AUDIO SPRITE MARKERS) - use _sfxSprite
  // ===========================================================

  /**
   * Play a short sound (marker) from the audio sprite.
   * Uses dedicated _sfxSprite instance so SFX won't interrupt music.
   * For loop=true, it creates a dedicated audio instance so that other SFX won't stop it.
   * @param marker The marker name in the audio sprite.
   * @param args Optional parameters for block time, pitch, loop.
   * @returns Unique id for this play instance.
   */
  public static playSound(
    marker: string,
    args?: { blockTime?: number; pitchChange?: boolean; loop?: boolean }
  ): string | null {
    console.log("AudioManager.playSound", marker, args);
    const self = this.instance;
    if (!args) args = { blockTime: 1000, pitchChange: false, loop: false };

    const now = Date.now();
    self._playingSounds = self._playingSounds.filter(
      (c) => now - c.lastPlayedTime < Math.max(1000, c.pitchChangeDelay)
    );

    const id = `${marker}_${now}_${Math.floor(Math.random() * 10000)}`;
    const entry: PlayingSoundEntry = {
      id,
      marker,
      blockTime: args.blockTime ?? 1000,
      lastPlayedTime: now,
      pitch: 0,
      pitchChangeDelay: args.pitchChange ? 1000 : args.blockTime ?? 1000,
      sound: null,
    };

    try {
      if (args.loop) {
        // Create a dedicated sprite instance for this looping sound so it won't be interrupted.
        const loopSprite = self._sound.addAudioSprite(Assets.AUDIOSPRITE_SOUNDS.key) as unknown as Phaser.Sound.BaseSound;
        loopSprite.play(marker, {
          volume: self._isMuted ? 0 : 1,
          detune: entry.pitch,
          loop: true,
        } as Phaser.Types.Sound.SoundConfig);

        entry.sound = loopSprite;
        self._playingSounds.push(entry);
        return id;
      } else {
        // Non-looping: reuse the shared SFX sprite (efficient)
        if (!self._sfxSprite) {
          self._sfxSprite = self._sound.addAudioSprite(Assets.AUDIOSPRITE_SOUNDS.key) as unknown as Phaser.Sound.BaseSound;
        }
        const sprite = self._sfxSprite;
        if (!sprite) {
          console.warn("AudioManager.playSound: failed to get SFX audio sprite.");
          return null;
        }
        sprite.play(marker, {
          volume: self._isMuted ? 0 : 1,
          detune: entry.pitch,
          loop: false,
        } as Phaser.Types.Sound.SoundConfig);

        entry.sound = sprite; // shared reference
        self._playingSounds.push(entry);
        return id;
      }
    } catch (err) {
      console.error("AudioManager.playSound error:", err);
      return null;
    }
  }

  /** Stop all currently playing sounds for the given marker (SFX). */
  /** Stop all currently playing instances for the given marker (robust). */
public static stopSound(marker: string): void {
  const self = this.instance;

  // 1) Stop tracked entries (both shared and dedicated instances)
  const entries = self._playingSounds.filter((s) => s.marker === marker);
  for (const entry of entries) {
    if (entry.sound) {
      try {
        // stop
        entry.sound.stop();

        // if it's a dedicated instance (not the shared _sfxSprite/_musicSprite), destroy it
        if (entry.sound !== self._sfxSprite && entry.sound !== self._musicSprite) {
          try { entry.sound.destroy(); } catch (e) { /* ignore */ }
        }
      } catch (err) {
        console.warn("AudioManager.stopSound (entry) error:", err);
      }
    }
  }

  // Remove all tracked entries for this marker
  self._playingSounds = self._playingSounds.filter((s) => s.marker !== marker);

  // 2) If music is playing this marker, stop it
  try {
    const musicCur = (self._music as any)?.currentMarker ?? (self._music as any)?._currentMarker ?? null;
    if (self._music && (musicCur === marker)) {
      try { self._music.stop(); } catch (e) {}
      try { self._music.destroy(); } catch (e) {}
      self._music = null;
    }
  } catch (e) { /* ignore */ }

  // 3) If the shared SFX sprite is currently playing that marker, stop it
  try {
    if (self._sfxSprite) {
      const sfxCur = (self._sfxSprite as any)?.currentMarker ?? (self._sfxSprite as any)?._currentMarker ?? null;
      if (sfxCur === marker) {
        try { self._sfxSprite.stop(); } catch (e) {}
      }
    }
  } catch (e) { /* ignore */ }

  // 4) Defensive: scan all sounds managed by the game sound manager and stop any playing that match the marker/key
  try {
    const allSounds = (self._sound as any).sounds as Array<any> | undefined;
    if (allSounds && allSounds.length) {
      for (const s of allSounds) {
        try {
          // check common fields across Phaser versions
          const cur = s.currentMarker ?? s._currentMarker ?? s.marker ?? s.key ?? null;
          const isPlaying = !!s.isPlaying;
          if (isPlaying && (cur === marker || s.key === marker)) {
            try { s.stop(); } catch (e) {}
            // if this looks like a dedicated instance created for looping, destroy it
            if (s !== self._sfxSprite && s !== self._musicSprite) {
              try { s.destroy(); } catch (e) {}
            }
          }
        } catch (innerErr) { /* ignore per-sound errors */ }
      }
    }
  } catch (err) {
    console.warn("AudioManager.stopSound: error scanning sound manager:", err);
  }
}
 /** Stop a specific play instance by its id (robust destruction). */
public static stopById(id: string): void {
  const self = this.instance;
  const idx = self._playingSounds.findIndex((s) => s.id === id);
  if (idx === -1) return;

  const entry = self._playingSounds[idx];
  if (entry.sound) {
    try {
      entry.sound.stop();
      if (entry.sound !== self._sfxSprite && entry.sound !== self._musicSprite) {
        try { entry.sound.destroy(); } catch (e) {}
      }
    } catch (err) {
      console.warn("AudioManager.stopById error:", err);
    }
  }

  self._playingSounds.splice(idx, 1);
}

  /** Stop all managed SFX. */
  public static stopAll(): void {
    const self = this.instance;
    for (const entry of self._playingSounds) {
      if (entry.sound) {
        try {
          entry.sound.stop();
        } catch {}
      }
    }
    self._playingSounds = [];
  }

  // ===========================================================
  // 🎵 BACKGROUND MUSIC - use _musicSprite separate from _sfxSprite
  // ===========================================================

  /**
   * Play looping background music.
   * Prefer to store music as a separate audio file and call playMusic with its key.
   * If music is a marker in the same audioplayer, this creates a dedicated music audio-sprite instance.
   * @param markerOrKey Marker name in audio sprite or audio key.
   * @param loop Whether to loop the music.
   * @param volume Volume (0–1).
   */
  public static playMusic(markerOrKey: string, loop = true, volume = 1.0): void {
  const self = this.instance;

  // stop existing music
  if (self._music) {
    try { self._music.stop(); self._music.destroy(); } catch {}
    self._music = null;
  }

  // Always try to create a fresh music sprite instance (avoid re-using a possibly-stale instance)
  let sprite: Phaser.Sound.BaseSound | null = null;
  try {
    sprite = self._sound.addAudioSprite(Assets.AUDIOSPRITE_SOUNDS.key) as unknown as Phaser.Sound.BaseSound;
  } catch (err) {
    console.warn('AudioManager.playMusic: addAudioSprite threw', err);
    sprite = null;
  }

  // Helper: check if audio-sprite has the marker
  const markerExistsOnSprite = (spr: any, m: string): boolean => {
    if (!spr) return false;
    if (spr.audioSprite && spr.audioSprite.data && spr.audioSprite.data[m]) return true;
    if (spr.audioSprite && spr.audioSprite._data && spr.audioSprite._data[m]) return true;
    if (spr.markers && spr.markers[m]) return true;
    if (spr._markers && spr._markers[m]) return true;
    if (spr.audioSprite && spr.audioSprite.sprites && spr.audioSprite.sprites[m]) return true;
    return false;
  };

  // 1) If sprite exists and contains marker, play marker
  if (sprite && markerExistsOnSprite(sprite as any, markerOrKey)) {
    try {
      sprite.play(markerOrKey, { loop, volume } as Phaser.Types.Sound.SoundConfig);
      self._musicSprite = sprite;
      self._music = sprite;
      return;
    } catch (err) {
      console.error('AudioManager.playMusic: failed to play marker on sprite', markerOrKey, err);
      try { sprite.destroy(); } catch {}
      sprite = null;
    }
  } else {
    // if sprite exists but marker not found — destroy it to avoid reusing it
    if (sprite) {
      try { sprite.destroy(); } catch {}
      sprite = null;
    }
  }

  // 2) Fallback: maybe markerOrKey is actually a standalone audio key (not a marker)
  try {
    let musicSound = self._sound.get(markerOrKey) as Phaser.Sound.BaseSound | undefined;
    if (!musicSound) {
      try {
        // try to add as a single audio key
        // some Phaser versions require different signatures; keep try/catch
        // @ts-ignore
        musicSound = self._sound.add(markerOrKey) as Phaser.Sound.BaseSound;
      } catch (err) {
        musicSound = undefined;
      }
    }

    if (musicSound) {
      musicSound.play({ loop, volume } as Phaser.Types.Sound.SoundConfig);
      self._music = musicSound;
      self._musicSprite = null; // we used a plain sound, not an audio-sprite
      return;
    }
  } catch (err) {
    console.warn('AudioManager.playMusic: fallback play as key failed', markerOrKey, err);
  }

  // 3) Nothing worked — helpful debug
  console.error(
    'AudioManager.playMusic error: marker/key not found or audio not decoded.',
    { markerOrKey, hasSprite: !!sprite, spriteInfo: sprite ? (sprite as any).audioSprite ?? (sprite as any).markers ?? (sprite as any)._markers : null }
  );
}


  /** Stop currently playing background music. */
  public static stopMusic(): void {
    const self = this.instance;
    if (self._music) {
      try {
        self._music.stop();
        self._music.destroy();
      } catch {}
      self._music = null;
    }

    // Note: keep _musicSprite if you plan to reuse it. Destroy it only if you want to free resources.
  }

  // ===========================================================
  // 🧭 MUTE / UNMUTE API
  // ===========================================================

  /** Mute all game sounds. */
  public static mute(): void {
    console.log("AudioManager.mute()");
    const self = this.instance;
    self._isMuted = true;
    self._sound.mute = true;
    if (self._onMuteStatusChange) self._onMuteStatusChange(true);
  }

  /** Unmute all game sounds. */
  public static unmute(): void {
    console.log("AudioManager.unmute()");
    const self = this.instance;
    self._isMuted = false;
    self._sound.mute = false;
    if (self._onMuteStatusChange) self._onMuteStatusChange(false);
  }

  /** Set global mute/unmute (alias) */
  public static setMuted(muted: boolean): void {
    if (muted) this.mute(); else this.unmute();
  }

  /** Whether audio is currently muted (read-only property). */
  public static get isMuted(): boolean {
    return this.instance._isMuted;
  }

  /** Debug helper */
  public static debugList(): PlayingSoundEntry[] {
    return [...this.instance._playingSounds];
  }
}
